import { Matrix4, Ray, Vector3, type CameraView } from '@/math'
import type { Vec3 } from '@/voxelTypes';

function toVector3(v: Vec3 | Vector3): Vector3 {
  return v instanceof Vector3 ? v : new Vector3(v.x, v.y, v.z)
}

/**
 * Computes the scalar parameter s for the closest point on a world-space line
 * (origin + s * dir) to the pointer ray derived from the event.
 */
export function closestLineParamFromPointer(
  ev: PointerEvent,
  camera: CameraView,
  rect: any,
  origin: Vec3 | Vector3,
  dir: Vec3 | Vector3
): number {
  const ndcX = ((ev.clientX - rect.left) / rect.width) * 2 - 1
  const ndcY = -(((ev.clientY - rect.top) / rect.height) * 2 - 1)

  const ray = new Ray()
  const proj = (camera as any).projectionMatrix
  const invProjView = new Matrix4().multiplyMatrices(
    (camera as any).matrixWorld,
    new Matrix4().copy(proj).invert()
  )
  const pNear = new Vector3(ndcX, ndcY, -1).applyMatrix4(invProjView)
  const pFar = new Vector3(ndcX, ndcY, 1).applyMatrix4(invProjView)
  ray.origin.copy(pNear)
  ray.direction.copy(pFar.sub(pNear).normalize())

  const P = toVector3(origin)
  const u = toVector3(dir).clone().normalize() // Unit direction for 1 unit = 1 voxel step
  const Q = ray.origin
  const v = ray.direction

  const w0 = new Vector3().subVectors(P, Q)
  const a = u.dot(u) // = 1 with normalization
  const b = u.dot(v)
  const c = v.dot(v)
  const d = u.dot(w0)
  const e = v.dot(w0)
  const denom = a * c - b * b

  let s = 0
  if (Math.abs(denom) > 1e-6) {
    s = (b * e - c * d) / denom
  } else {
    // Parallel → project w0 onto u
    s = d / (a || 1)
  }
  return s
}

/**
 * Snaps value to step and clamps to [min, max].
 */
export function snapClamp(value: number, step: number, min: number, max: number): number {
  const snapped = Math.round(value / step) * step
  if (snapped < min) return min
  if (snapped > max) return max
  return snapped
}

/**
 * Returns a stable beam direction within the plane perpendicular to the given axis,
 * based on the current camera orientation.
 */
export function beamDirForRadius(axis: 'x'|'y'|'z', camera: CameraView): Vector3 {
  const camRight = new Vector3(1, 0, 0).applyQuaternion((camera as any).quaternion)
  const camUp = new Vector3(0, 1, 0).applyQuaternion((camera as any).quaternion)
  const n = axis === 'x' ? new Vector3(1, 0, 0)
          : axis === 'y' ? new Vector3(0, 1, 0)
                          : new Vector3(0, 0, 1)
  const project = (v: Vector3) => v.clone().sub(n.clone().multiplyScalar(v.dot(n)))
  let dir = project(camRight)
  if (dir.lengthSq() < 1e-4) dir = project(camUp)
  if (dir.lengthSq() < 1e-4) {
    if (axis === 'y') dir.set(1, 0, 0)
    else if (axis === 'x') dir.set(0, 1, 0)
    else dir.set(1, 0, 0)
  }
  return dir.normalize()
}